/**
 * Renders an input widget for a single template variable. Switches form by
 * variable.type — text, longText, number, currency, percent, date, enum,
 * scopeList (array of strings), milestoneList (array of structured rows).
 *
 * Structured types use their own dedicated editors so the user can add /
 * remove / reorder rows without touching JSON. The resolved value is always
 * a plain JS value (string / number / array / etc.) — never a JSON string.
 */

import { Trash2, Plus, GripVertical, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { TemplateVariable, PaymentMilestoneValue } from '@/types/table';

interface VariableInputProps {
  variable: TemplateVariable;
  value: unknown;
  onChange: (next: unknown) => void;
  /** Whether this field is hidden from the document. Only meaningful for optional fields. */
  hidden?: boolean;
  onToggleHidden?: () => void;
}

export function VariableInput({ variable, value, onChange, hidden, onToggleHidden }: VariableInputProps) {
  const canHide = !variable.required && !!onToggleHidden;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className={`text-sm ${hidden ? 'text-muted-foreground line-through' : ''}`}>
          {variable.label}
          {variable.required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <div className="flex items-center gap-1.5">
          {variable.source && variable.source !== 'manual' && (
            <Badge variant="outline" className="text-[10px]">auto: {sourceLabel(variable.source)}</Badge>
          )}
          {canHide && (
            <button
              type="button"
              onClick={onToggleHidden}
              title={hidden ? 'Show in document' : 'Hide from document'}
              className="text-muted-foreground hover:text-foreground"
            >
              {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          )}
        </div>
      </div>
      {variable.description && !hidden && <p className="text-xs text-muted-foreground">{variable.description}</p>}
      {hidden
        ? <p className="text-xs text-muted-foreground italic">Hidden — won't appear in the document.</p>
        : renderWidget(variable, value, onChange)}
    </div>
  );
}

function sourceLabel(src: string): string {
  switch (src) {
    case 'org':          return 'org settings';
    case 'orgSignatory': return 'signature';
    case 'orgPolicy':    return 'org defaults';
    case 'contact':      return 'contact';
    case 'company':      return 'company';
    case 'agreement':    return 'agreement';
    default:             return src;
  }
}

function renderWidget(v: TemplateVariable, value: unknown, onChange: (n: unknown) => void) {
  switch (v.type) {
    case 'text':
      return <Input value={(value as string) || ''} onChange={(e) => onChange(e.target.value)} />;
    case 'longText':
      return <Textarea value={(value as string) || ''} onChange={(e) => onChange(e.target.value)} rows={3} />;
    case 'number':
    case 'percent':
      return <Input type="number" step="any" value={(value as number) ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
    case 'currency': {
      const v2 = (value as { amount?: number; currency?: string }) || {};
      return (
        <Input
          type="number"
          step="0.01"
          value={v2.amount ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : { amount: Number(e.target.value), currency: v2.currency || 'USD' })}
        />
      );
    }
    case 'date':
      return <Input type="date" className="cursor-pointer dark:[color-scheme:dark]" value={toDateInputValue(value)} onChange={(e) => onChange(e.target.value)} />;
    case 'enum':
      return (
        <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={(value as string) || ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {(v.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case 'scopeList':
      return <ScopeListEditor value={(value as string[]) || []} onChange={onChange} />;
    case 'milestoneList':
      return <MilestoneListEditor value={(value as PaymentMilestoneValue[]) || []} onChange={onChange} />;
    default:
      return <Input value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />;
  }
}

function toDateInputValue(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v.length >= 10 ? v.slice(0, 10) : v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}

// ─────────────────────────────────────────────────────────────────────

function ScopeListEditor({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  return (
    <div className="space-y-1.5">
      {value.length === 0 && <div className="text-xs text-muted-foreground rounded-md border border-dashed p-3 text-center">No items yet. Add a line per deliverable.</div>}
      {value.map((item, i) => (
        <div key={i} className="flex gap-1 items-center">
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <Input value={item} onChange={(e) => onChange(value.map((x, j) => j === i ? e.target.value : x))} placeholder={`Deliverable ${i + 1}`} />
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, ''])}>
        <Plus className="mr-1 h-3.5 w-3.5" />Add item
      </Button>
    </div>
  );
}

function MilestoneListEditor({ value, onChange }: { value: PaymentMilestoneValue[]; onChange: (next: PaymentMilestoneValue[]) => void }) {
  const total = value.reduce((s, m) => s + (Number(m.percent) || 0), 0);
  return (
    <div className="space-y-2">
      {value.length === 0 && <div className="text-xs text-muted-foreground rounded-md border border-dashed p-3 text-center">No milestones yet.</div>}
      {value.map((m, i) => (
        <div key={i} className="grid grid-cols-[1fr_90px_140px_auto] gap-1.5 items-center">
          <Input value={m.name} onChange={(e) => onChange(value.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder={`Milestone ${i + 1} (e.g. On signing)`} />
          <Input type="number" step="any" value={m.percent ?? ''} onChange={(e) => onChange(value.map((x, j) => j === i ? { ...x, percent: e.target.value === '' ? 0 : Number(e.target.value) } : x))} placeholder="%" />
          <Input type="date" className="cursor-pointer dark:[color-scheme:dark]" value={m.dueDate ?? ''} onChange={(e) => onChange(value.map((x, j) => j === i ? { ...x, dueDate: e.target.value || undefined } : x))} />
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8" onClick={() => onChange(value.filter((_, j) => j !== i))}>
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { name: '', percent: 0 }])}>
          <Plus className="mr-1 h-3.5 w-3.5" />Add milestone
        </Button>
        <span className={`text-xs ${total === 100 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}>
          Total: {total}% {total !== 100 && '(should equal 100%)'}
        </span>
      </div>
    </div>
  );
}
